import { FEATURE_FLAGS } from "../../config/features";
import { clamp } from "../../utils/math";
import type { ProgressStats } from "../save/types";

export interface ProfileProgress {
  readonly xp: number;
  readonly level: number;
  readonly prestigeXp: number;
  readonly prestigeLevel: number;
  readonly effectiveLevel: number;
  readonly title: string;
  readonly levelProgressPercent: number;
  readonly xpIntoLevel: number;
  readonly xpForNextLevel: number;
}

export function calculateXp(stats: ProgressStats): number {
  return (
    stats.TotalEarnedCredits ** 0.43 * 8.35 +
    stats.TotalCoresCollected * 0.56 +
    stats.TotalChargeBounces * 0.046 +
    Math.min(35_000, stats.FluxHockeyXp)
  );
}

export function xpForLevel(level: number): number {
  const clamped = clamp(level, 1, 40);
  if (clamped === 1) {
    return 0;
  }

  if (clamped === 2) {
    return 352;
  }

  const n = clamped - 1;
  const late = Math.max(0, clamped - 30);
  return n ** 2 * 430 + n ** 3 * 27.5 + late ** 2 * 76_000;
}

export function prestigeXpForLevel(prestigeLevel: number): number {
  const clamped = clamp(prestigeLevel, 1, 40);
  const n = clamped - 1;
  return n ** 2 * 95_000 + n ** 3 * 9_800;
}

export function lookupProfileProgress(stats: ProgressStats): ProfileProgress {
  const xp = calculateXp(stats);
  let level = 1;

  while (level < 40 && xp >= xpForLevel(level + 1)) {
    level += 1;
  }

  const prestigeXp = FEATURE_FLAGS.prestige ? Math.max(0, xp - xpForLevel(40)) : 0;
  let prestigeLevel = FEATURE_FLAGS.prestige && level === 40 ? 1 : 0;

  while (prestigeLevel > 0 && prestigeLevel < 40 && prestigeXp >= prestigeXpForLevel(prestigeLevel + 1)) {
    prestigeLevel += 1;
  }

  const effectiveLevel = prestigeLevel > 0 ? 40 : level;
  const currentLevelFloor = prestigeLevel > 0 ? prestigeXpForLevel(prestigeLevel) : xpForLevel(level);
  const nextLevelTarget = prestigeLevel > 0 ? prestigeXpForLevel(Math.min(40, prestigeLevel + 1)) : xpForLevel(Math.min(40, level + 1));
  const progressXp = prestigeLevel > 0 ? prestigeXp : xp;
  const xpIntoLevel = Math.max(0, progressXp - currentLevelFloor);
  const xpForNextLevel = Math.max(0, nextLevelTarget - currentLevelFloor);
  const levelProgressPercent = xpForNextLevel > 0 ? clamp((xpIntoLevel / xpForNextLevel) * 100, 0, 100) : 100;

  return {
    xp,
    level,
    prestigeXp,
    prestigeLevel,
    effectiveLevel,
    title: titleForLevel(level, prestigeLevel),
    levelProgressPercent,
    xpIntoLevel,
    xpForNextLevel
  };
}

function titleForLevel(level: number, prestigeLevel: number): string {
  if (prestigeLevel > 0) {
    return prestigeTitle(prestigeLevel);
  }

  const title = normalTitles.find((entry) => level >= entry.min && level <= entry.max);
  return title?.title ?? "Spark Initiate";
}

const normalTitles: readonly { readonly min: number; readonly max: number; readonly title: string }[] = [
  { min: 1, max: 2, title: "Spark Initiate" },
  { min: 3, max: 4, title: "Core Clicker" },
  { min: 5, max: 6, title: "Shell Rookie" },
  { min: 7, max: 9, title: "Charge Runner" },
  { min: 10, max: 11, title: "Collector Driver" },
  { min: 12, max: 14, title: "Flux Grinder" },
  { min: 15, max: 16, title: "Reactor Climber" },
  { min: 17, max: 19, title: "Shell Artist" },
  { min: 20, max: 21, title: "Arcade Engineer" },
  { min: 22, max: 24, title: "Circuit Legend" },
  { min: 25, max: 27, title: "Collector Banker" },
  { min: 28, max: 29, title: "Neon Engineer" },
  { min: 30, max: 32, title: "Reactor Master" },
  { min: 33, max: 34, title: "Idle Architect" },
  { min: 35, max: 37, title: "Orbit Legend" },
  { min: 38, max: 40, title: "Forge King" }
];

function prestigeTitle(level: number): string {
  if (level <= 1) return "Prestige Starter";
  if (level <= 2) return "Golden Rookie";
  if (level <= 3) return "Golden Clicker";
  if (level <= 4) return "Golden Driver";
  if (level <= 5) return "Golden Banker";
  if (level <= 6) return "Neon Prestige";
  if (level <= 7) return "Prestige Grinder";
  if (level <= 8) return "Royal Launcher";
  if (level <= 9) return "Golden Shell";
  if (level <= 10) return "Credit Crown";
  if (level <= 11) return "Neon Baron";
  if (level <= 12) return "Elite Engineer";
  if (level <= 13) return "Orbit Elite";
  if (level <= 14) return "Royal Circuit";
  if (level <= 15) return "Prestige Legend";
  if (level <= 16) return "Golden Master";
  if (level <= 17) return "Neon Royal";
  if (level <= 18) return "Reactor Royal";
  if (level <= 19) return "Golden King";
  if (level <= 21) return "Orbit VIP";
  if (level <= 23) return "Neon Regent";
  if (level <= 25) return "Golden Titan";
  if (level <= 27) return "Radiant Legend";
  if (level <= 29) return "Orbit Emperor";
  if (level <= 31) return "Jackpot Royal";
  if (level <= 33) return "Prestige Tycoon";
  if (level <= 35) return "Crowned Forger";
  if (level <= 37) return "Neon Sovereign";
  if (level <= 39) return "Golden Ascendant";
  return "Forge Immortal";
}
